import { useEffect, useState } from 'react';

export interface DsstViewportMetrics {
  height: number;
  screenPt: number;
  screenPb: number;
  symbolSize: number;
  mapSymbol: number;
}

// 실제 사용 가능한 viewport 높이를 읽어 DSST 게임 영역 레이아웃 지표를 계산한다.
// 브라우저 주소창/탭바 등 UI 변화는 visualViewport.height 로 감지한다.
function computeMetrics(): DsstViewportMetrics {
  const visualViewport =
    typeof window !== 'undefined' ? window.visualViewport : undefined;
  const height = Math.round(
    visualViewport?.height ??
      (typeof window !== 'undefined' ? window.innerHeight : 800),
  );

  let screenPt: number;
  let screenPb: number;
  let symbolSize: number;
  let mapSymbol: number;

  if (height >= 780) {
    screenPt = 20;
    screenPb = 16;
    symbolSize = 88;
    mapSymbol = 32;
  } else if (height >= 660) {
    screenPt = 14;
    screenPb = 12;
    symbolSize = 76;
    mapSymbol = 28;
  } else if (height >= 560) {
    screenPt = 10;
    screenPb = 8;
    symbolSize = 62;
    mapSymbol = 24;
  } else {
    screenPt = 6;
    screenPb = 6;
    symbolSize = 50;
    mapSymbol = 22;
  }

  return { height, screenPt, screenPb, symbolSize, mapSymbol };
}

// CSS 변수를 문서 루트에 주입해 하위 컴포넌트가 var(--dsst-*)로 소비하게 한다.
function applyMetrics(m: DsstViewportMetrics): void {
  const root = document.documentElement.style;
  root.setProperty('--dsst-pt', `${m.screenPt}px`);
  root.setProperty('--dsst-pb', `${m.screenPb}px`);
  root.setProperty('--dsst-symbol-size', `${m.symbolSize}px`);
  root.setProperty('--dsst-map-symbol', `${m.mapSymbol}px`);
}

export function useViewportMetrics(): DsstViewportMetrics {
  const [metrics, setMetrics] = useState<DsstViewportMetrics>(() => computeMetrics());

  useEffect(() => {
    const visualViewport = window.visualViewport;
    const update = () => {
      const next = computeMetrics();
      setMetrics(next);
      applyMetrics(next);
    };

    update();

    visualViewport?.addEventListener('resize', update);
    visualViewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);

    return () => {
      visualViewport?.removeEventListener('resize', update);
      visualViewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return metrics;
}